import { WEBSITE_LINKS } from '@/app/lib/constants'
import * as CookieConsent from 'vanilla-cookieconsent'

type JsonObject = Record<string, unknown>

export const CAT_NECESSARY = 'necessary'
export const CAT_ANALYTICS = 'analytics'
export const CAT_ADVERTISEMENT = 'advertisement'
export const CAT_FUNCTIONALITY = 'functionality'
export const CAT_SECURITY = 'security'

export const SERVICE_AD_STORAGE = 'ad_storage'
export const SERVICE_AD_USER_DATA = 'ad_user_data'
export const SERVICE_AD_PERSONALIZATION = 'ad_personalization'
export const SERVICE_ANALYTICS_STORAGE = 'analytics_storage'
export const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage'
export const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage'
export const SERVICE_SECURITY_STORAGE = 'security_storage'

export function initializeDataLayerWithDefaultConsent() {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window!.dataLayer.push(arguments)
  }

  window.gtag = window.gtag || gtag

  // Set default consent to 'denied' (this should happen before changing any other dataLayer)
  window.gtag('consent', 'default', {
    [SERVICE_AD_STORAGE]: 'denied',
    [SERVICE_AD_USER_DATA]: 'denied',
    [SERVICE_AD_PERSONALIZATION]: 'denied',
    [SERVICE_ANALYTICS_STORAGE]: 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: 'denied',
    [SERVICE_SECURITY_STORAGE]: 'denied',
  })
}

function updateGtagConsent() {
  window.gtag('consent', 'update', {
    [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(
      SERVICE_ANALYTICS_STORAGE,
      CAT_ANALYTICS
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(
      SERVICE_AD_STORAGE,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(
      SERVICE_AD_USER_DATA,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(
      SERVICE_AD_PERSONALIZATION,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_FUNCTIONALITY_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? 'granted'
      : 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(
      SERVICE_PERSONALIZATION_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? 'granted'
      : 'denied',
    [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_SECURITY_STORAGE,
      CAT_SECURITY
    )
      ? 'granted'
      : 'denied',
  })
}

const domain = window.location.hostname

export const COOKIE_CONSENT_CONFIG: CookieConsent.CookieConsentConfig = {
  root: 'body',
  mode: 'opt-in',
  autoShow: true,
  revision: 0,
  manageScriptTags: true,
  autoClearCookies: true,
  hideFromBots: true,
  disablePageInteraction: false,
  lazyHtmlGeneration: true,

  guiOptions: {
    consentModal: {
      layout: 'cloud inline',
      position: 'bottom center',
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  onFirstConsent: () => {
    updateGtagConsent()
  },
  onConsent: () => {
    updateGtagConsent()
  },
  onChange: () => {
    updateGtagConsent()
  },

  categories: {
    [CAT_NECESSARY]: {
      enabled: true,
      readOnly: true,
    },
    [CAT_ANALYTICS]: {
      autoClear: {
        cookies: [
          {
            name: /^(_ga|_gid)/,
          },
          {
            name: /^_gat/,
          },
          {
            name: /^AMP_TOKEN/,
          },
          {
            name: /^gac/,
          },
        ],
      },
      // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
      services: {
        [SERVICE_ANALYTICS_STORAGE]: {
          label:
            'Enables storage (such as cookies) related to analytics e.g. visit duration.',
        },
      },
    },
    [CAT_ADVERTISEMENT]: {
      services: {
        [SERVICE_AD_STORAGE]: {
          label: 'Enables storage (such as cookies) related to advertising.',
        },
        [SERVICE_AD_USER_DATA]: {
          label:
            'Sets consent for sending user data related to advertising to Google.',
        },
        [SERVICE_AD_PERSONALIZATION]: {
          label: 'Sets consent for personalized advertising.',
        },
      },
    },
    [CAT_FUNCTIONALITY]: {
      services: {
        [SERVICE_FUNCTIONALITY_STORAGE]: {
          label:
            'Enables storage that supports the functionality of the website or app e.g. language settings.',
        },
        [SERVICE_PERSONALIZATION_STORAGE]: {
          label:
            'Enables storage related to personalization e.g. video recommendations.',
        },
      },
    },
    [CAT_SECURITY]: {
      services: {
        [SERVICE_SECURITY_STORAGE]: {
          label:
            'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
        },
      },
    },
  },
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          description:
            'This website uses essential cookies to ensure its proper operation and tracking cookies to understand how you interact with it. The latter will be set only after consent.',
          footer: `<a href="${WEBSITE_LINKS.privacyPolicy}" target="_blank">Privacy Policy</a>`,
          showPreferencesBtn: 'Manage Individual preferences',
          title: 'We use cookies',
        },
        preferencesModal: {
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Reject all',
          closeIconLabel: 'Close modal',
          savePreferencesBtn: 'Accept current selection',
          sections: [
            {
              description:
                'We use cookies to ensure the basic functionalities of the website and to enhance your online experience.',
              title: 'Cookie usage',
            },
            {
              description:
                'Cookies used for functionality allow users to interact with a service or site to access features that are fundamental to that service. Things considered fundamental to the service include preferences like the user’s choice of language, product optimizations that help maintain and improve a service, and maintaining information relating to a user’s session, such as the content of a shopping cart.',
              linkedCategory: CAT_FUNCTIONALITY,
              title: 'Functionality',
            },
            {
              cookieTable: {
                body: [
                  {
                    description:
                      'The workspace id cookie, used to store your current workspace',
                    domain,
                    name: 'mydraworkspaceid*',
                  },
                  {
                    description:
                      "The locale of the user, used to display the website in the user's language",
                    domain,
                    name: 'NEXT_LOCALE',
                  },
                  {
                    description:
                      'The auth cookie, used to authenticate the user, and store basic user information',
                    domain,
                    name: 'mydraauth*',
                  },
                  {
                    description:
                      "The cookie consent cookie, used to store the user's cookie consent preferences",
                    domain,
                    name: 'cc_cookie',
                  },
                  {
                    description:
                      'The sidebar state cookie, used to store the state of the sidebar',
                    domain,
                    name: 'sidebar_collapsed',
                  },
                ],
                headers: {
                  description: 'Description',
                  domain: 'Domain',
                  name: 'Name',
                },
              },
              description:
                'These cookies are essential for the proper functioning of the website, for example for user authentication.',
              linkedCategory: CAT_NECESSARY,
              title: 'Strictly necessary cookies',
            },
            {
              description:
                'Cookies used for analytics help collect data that allows services to understand how users interact with a particular service. These insights allow services both to improve content and to build better features that improve the user’s experience.',
              linkedCategory: CAT_ANALYTICS,
              title: 'Analytics',
            },
            {
              description:
                'Google uses cookies for advertising, including serving and rendering ads, personalizing ads (depending on your ad settings at <a href="https://g.co/adsettings">g.co/adsettings</a>), limiting the number of times an ad is shown to a user, muting ads you have chosen to stop seeing, and measuring the effectiveness of ads.',
              linkedCategory: CAT_ADVERTISEMENT,
              title: 'Advertising',
            },
            {
              description:
                'Cookies used for security authenticate users, prevent fraud, and protect users as they interact with a service.',
              linkedCategory: CAT_SECURITY,
              title: 'Security',
            },
            {
              description:
                'For any queries in relation to the policy on cookies and your choices, please <a href="mailto:support@mydra.io">contact us</a>.',
              title: 'More information',
            },
          ],
          serviceCounterLabel: 'Service|Services',
          title: 'Manage cookie preferences',
        },
      },
    },
  },
}
